import React, { useState } from "react";
import { Eye, Upload, Trash2, Image as ImageIcon, Sparkles } from "lucide-react";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";

const WidgetPreview = () => {
  const { showToast } = useToast();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleImageUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please upload an image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size should be less than 5MB", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      setUploadedImage(base64Image);
      setImagePreview(base64Image);
      sessionStorage.setItem("widget_preview_background", base64Image);
      showToast("Background image updated!", "success");
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleImageUpload(file);
  };

  const handlePreview = () => {
    window.open("/widget-preview/display", "_blank");
  };

  const handleRemoveImage = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmRemoveImage = () => {
    setIsDeleting(true);
    setUploadedImage(null);
    setImagePreview(null);
    sessionStorage.removeItem("widget_preview_background");
    showToast("Background image removed", "info");
    setIsDeleteModalOpen(false);
    setIsDeleting(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start">
      <div className="w-full max-w-4xl space-y-12">
        <PageHeader 
          title="Vision Simulation"
          description="Test the architectural harmony of your assistant within its native environment."
        />

        <div className="grid gap-8 animate-slideIn">
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <ImageIcon size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Contextual Simulation</h3>
                  <p className="text-[11px] font-medium text-gray-400">Upload a screenshot of your target page</p>
                </div>
              </div>
            </div>

            {!uploadedImage ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() =>
                  document.getElementById("image-upload-input").click()
                }
                className="group border-2 border-dashed border-gray-100 rounded-[24px] p-16 text-center cursor-pointer bg-gray-50/50 hover:bg-white hover:border-blue-200 transition-all duration-300"
              >
                <div className="w-20 h-20 bg-white rounded-[28px] border border-gray-50 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-sm">
                   <Upload size={32} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
                </div>
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">
                  Drop your website screenshot
                </h4>
                <p className="text-sm text-gray-400 max-w-xs mx-auto leading-relaxed">
                  Upload a 1920x1080 capture of your site to see exactly how the widget integrates.
                </p>
                <div className="flex justify-center gap-2 mt-6">
                   <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[10px] font-bold tracking-wider uppercase">PNG</span>
                   <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[10px] font-bold tracking-wider uppercase">JPG</span>
                   <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[10px] font-bold tracking-wider uppercase">MAX 5MB</span>
                </div>
                <input
                  id="image-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="relative rounded-[24px] overflow-hidden border border-gray-100 shadow-2xl group">
                  <img
                    src={imagePreview}
                    alt="Preview background"
                    className="w-full max-h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-4 right-4 p-3 bg-white/90 backdrop-blur-md text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-xl"
                    title="Remove background"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handlePreview}
                    className="flex-1 flex items-center justify-center gap-3 px-8 py-5 bg-gray-900 text-white rounded-[24px] hover:bg-gray-800 transition-all font-bold shadow-xl shadow-gray-200"
                  >
                    <Eye size={20} />
                    Launch Live Preview
                  </button>
                </div>
              </div>
            )}
          </div>

          {!uploadedImage && (
            <div className="bg-gray-900 rounded-[32px] p-8 border border-gray-800 shadow-2xl relative overflow-hidden group">
              <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-amber-400" />
                    <h3 className="text-white font-bold text-xs uppercase tracking-widest tracking-tight">
                      Quick Preview
                    </h3>
                  </div>
                  <p className="text-gray-400 text-sm max-w-md leading-relaxed">
                    Don't have a screenshot ready? Preview the widget on a clean, minimal canvas to verify your brand colors and interactions.
                  </p>
                </div>
                <button
                  onClick={handlePreview}
                  className="px-8 py-4 bg-white text-gray-900 rounded-2xl hover:bg-gray-100 transition-all font-bold text-sm shadow-xl flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Eye size={18} />
                  Standard Preview
                </button>
              </div>
              <div className="absolute -right-8 -bottom-8 opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                <Eye size={160} className="text-white" strokeWidth={1} />
              </div>
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmRemoveImage}
        isLoading={isDeleting}
        confirmText="Remove Image"
        title="Un-Simulation Context?"
        message="This will decouple the background environment from the vision simulation. Are you prepared to proceed?"
      />
    </div>
  );
};

export default WidgetPreview;
